import React, { CSSProperties, useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, SlidersHorizontal } from 'lucide-react';
import { FilterTag } from '../../../App.enums';
import AddFilterDialog from '../../dialogs/AddFilterDialog/AddFilterDialog';
import TagBadge from '../TagBadge/TagBadge';

type SearchBoxNewProps = {
    onSearchChanged: (value: string) => void;
    onFilterChanged?: (tags: FilterTag[]) => void;
    onClientSearchTermsChanged?: (terms: string[]) => void;
    toolbarControls?: React.ReactNode;
    placeholder?: string;
    style?: CSSProperties;
    controlledValue?: string;
};

const SearchBoxNew = (props: SearchBoxNewProps) => {
    const { onSearchChanged, onFilterChanged, onClientSearchTermsChanged, toolbarControls, placeholder, style, controlledValue } = props;
    const [value, setValue] = useState('');
    const [tags, setTags] = useState<FilterTag[]>([]);
    const [filterVisible, setFilterVisible] = useState(false);
    const [searchChips, setSearchChips] = useState<string[]>([]);
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const MAX_CHIPS = 5;
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onSearchChangedRef = useRef(onSearchChanged);
    onSearchChangedRef.current = onSearchChanged;
    const onClientSearchTermsChangedRef = useRef(onClientSearchTermsChanged);
    onClientSearchTermsChangedRef.current = onClientSearchTermsChanged;
    const valueRef = useRef(value);
    valueRef.current = value;

    useEffect(() => {
        if (controlledValue !== undefined && controlledValue !== valueRef.current) {
            setValue(controlledValue);
            setSearchChips([]);
            // When controlled value changes externally, fire immediately (no debounce)
            if (debounceRef.current) clearTimeout(debounceRef.current);
            onSearchChangedRef.current(controlledValue);
            onClientSearchTermsChangedRef.current?.([]);
        }
    }, [controlledValue]);

    const debouncedSearch = useCallback((text: string) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            onSearchChangedRef.current(text);
        }, 300);
    }, []);

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setValue(newValue);
        if (searchChips.length > 0) {
            // Live input is an additional client-side AND filter
            const terms = [...searchChips.slice(1), newValue].filter(t => t.trim());
            onClientSearchTermsChangedRef.current?.(terms);
        } else {
            debouncedSearch(newValue);
        }
    };

    const handleClear = () => {
        setValue('');
        setSearchChips([]);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        onSearchChangedRef.current('');
        onClientSearchTermsChangedRef.current?.([]);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Tab' && value.trim()) {
            e.preventDefault();
            if (searchChips.length < MAX_CHIPS) {
                const newChips = [...searchChips, value.trim()];
                setSearchChips(newChips);
                setValue('');
                if (debounceRef.current) clearTimeout(debounceRef.current);
                if (newChips.length === 1) {
                    // First chip becomes the server search term
                    onSearchChangedRef.current(newChips[0]);
                }
                onClientSearchTermsChangedRef.current?.(newChips.slice(1));
                setTimeout(() => inputRef.current?.focus(), 0);
            }
        } else if (e.key === 'Backspace' && value === '' && searchChips.length > 0) {
            const newChips = searchChips.slice(0, -1);
            setSearchChips(newChips);
            if (newChips.length === 0) {
                onSearchChangedRef.current('');
                onClientSearchTermsChangedRef.current?.([]);
            } else {
                onClientSearchTermsChangedRef.current?.(newChips.slice(1));
            }
        }
    };

    const removeChip = (index: number) => {
        const newChips = searchChips.filter((_, i) => i !== index);
        setSearchChips(newChips);
        if (newChips.length === 0) {
            // No chips left — current input becomes the server search
            onClientSearchTermsChangedRef.current?.([]);
            if (debounceRef.current) clearTimeout(debounceRef.current);
            onSearchChangedRef.current(valueRef.current);
        } else if (index === 0) {
            // First chip removed — promote next chip to server search
            onSearchChangedRef.current(newChips[0]);
            const terms = [...newChips.slice(1), valueRef.current].filter(t => t.trim());
            onClientSearchTermsChangedRef.current?.(terms);
        } else {
            const terms = [...newChips.slice(1), valueRef.current].filter(t => t.trim());
            onClientSearchTermsChangedRef.current?.(terms);
        }
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const handleFilterClose = (t?: FilterTag) => {
        setFilterVisible(false);
        if (t && t.id) {
            setTags(prev => [...prev, t]);
        }
    };

    const handleRemoveTag = (tagId: string) => {
        setTags(prev => prev.filter(t => t.id !== tagId));
    };

    useEffect(() => {
        onFilterChanged && onFilterChanged(tags);
    }, [tags]);

    return (
        <div className="flex flex-col gap-2 px-4 py-2" style={style}>
            <div className="flex items-center gap-2">
                <div
                    className="flex-1 flex items-center gap-1.5"
                    style={{
                        backgroundColor: 'var(--surface-0)',
                        border: '1px solid',
                        borderColor: isFocused ? 'var(--accent)' : 'var(--border-default)',
                        borderRadius: '0.5rem',
                        padding: '0 0.75rem',
                        minHeight: '38px',
                        transition: 'all 150ms',
                        boxShadow: isFocused ? '0 0 0 3px rgba(77, 124, 15, 0.2)' : 'none',
                        cursor: 'text',
                    }}
                    onClick={() => inputRef.current?.focus()}
                >
                    <Search size={16} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', flex: 1, alignItems: 'center', minWidth: 0 }}>
                        {searchChips.map((chip, idx) => (
                            <span
                                key={idx}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '3px',
                                    padding: '2px 4px 2px 8px', borderRadius: '12px',
                                    fontSize: '0.72rem', fontWeight: 600,
                                    backgroundColor: 'var(--accent-soft)', color: 'var(--accent)',
                                    border: '1px solid var(--accent)', whiteSpace: 'nowrap', lineHeight: 1.5,
                                }}
                            >
                                {chip}
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); removeChip(idx); }}
                                    tabIndex={-1}
                                    title="Remove filter term"
                                    style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        width: '16px', height: '16px', border: 'none', background: 'none',
                                        cursor: 'pointer', color: 'inherit', padding: 0, borderRadius: '50%',
                                        opacity: 0.7,
                                    }}
                                    onMouseOver={(e) => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
                                    onMouseOut={(e) => { (e.currentTarget as HTMLElement).style.opacity = '0.7'; }}
                                >
                                    <X size={11} />
                                </button>
                            </span>
                        ))}
                        <input
                            ref={inputRef}
                            type="text"
                            value={value}
                            onChange={handleChange}
                            onKeyDown={handleKeyDown}
                            onFocus={() => setIsFocused(true)}
                            onBlur={() => setIsFocused(false)}
                            placeholder={
                                searchChips.length === 0
                                    ? (placeholder || 'Search...')
                                    : searchChips.length < MAX_CHIPS
                                        ? 'Add filter (Tab\u21b9)...'
                                        : ''
                            }
                            disabled={searchChips.length >= MAX_CHIPS}
                            style={{
                                flex: 1, minWidth: '80px', border: 'none', outline: 'none',
                                background: 'transparent', fontSize: '0.85rem',
                                color: 'var(--text-primary)', padding: '6px 0',
                            }}
                        />
                    </div>
                    {(value || searchChips.length > 0) && (
                        <button
                            onClick={handleClear}
                            tabIndex={-1}
                            className="p-1 rounded-md hover:opacity-80 transition-opacity"
                            style={{ color: 'var(--text-tertiary)', background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}
                        >
                            <X size={14} />
                        </button>
                    )}
                </div>
                {toolbarControls}
                {onFilterChanged && (
                    <button
                        onClick={() => setFilterVisible(true)}
                        className="btn-secondary flex items-center gap-2"
                        style={{ flexShrink: 0 }}
                    >
                        <SlidersHorizontal size={16} />
                        <span>Filter</span>
                        {tags.length > 0 && (
                            <span className="badge-accent ml-1">{tags.length}</span>
                        )}
                    </button>
                )}
            </div>

            {/* Active Filters */}
            {tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 animate-fade-in">
                    {tags.map(t => (
                        <TagBadge
                            key={t.id}
                            tag={t}
                            className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 text-xs font-medium rounded-full"
                            style={t.inverse ? {
                                backgroundColor: 'var(--error-soft, #fee2e2)',
                                color: 'var(--error-text, #b91c1c)',
                                border: '1px solid var(--error-border, #fca5a5)',
                            } : undefined}
                        >
                            {t.inverse && <span className="font-bold opacity-80">NOT</span>}
                            <span className="font-semibold">{t.type}:</span>
                            <span>{t.name}</span>
                            <button
                                onClick={() => handleRemoveTag(t.id || '')}
                                className="p-0.5 rounded-full hover:opacity-70 transition-opacity ml-0.5"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}
                            >
                                <X size={12} />
                            </button>
                        </TagBadge>
                    ))}
                    <button
                        onClick={() => setTags([])}
                        className="text-xs font-medium px-2 py-1 rounded-full transition-colors"
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--text-tertiary)',
                        }}
                    >
                        Clear all
                    </button>
                </div>
            )}

            {filterVisible && <AddFilterDialog visible={filterVisible} onClose={handleFilterClose} />}
        </div>
    );
};

export default SearchBoxNew;
